import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Heading, Text, VStack, HStack, Icon, Code } from '@chakra-ui/react';
import { AlertTriangle } from 'lucide-react';

type DesignErrorBoundaryProps = {
	children: ReactNode;
};

type DesignErrorBoundaryState = {
	hasError: boolean;
	error: Error | null;
};

/**
 * Displays a user-friendly message allowing users to continue without losing work.
 */
export class DesignErrorBoundary extends Component<DesignErrorBoundaryProps, DesignErrorBoundaryState> {
	constructor(props: DesignErrorBoundaryProps) {
		super(props);
		this.state = {
			hasError: false,
			error: null
		};
	}

	static getDerivedStateFromError(error: Error): Partial<DesignErrorBoundaryState> {
		return {
			hasError: true,
			error
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		console.error('DesignErrorBoundary caught error:', error, errorInfo);
	}

	render(): ReactNode {
		if (!this.state.hasError) {
			return this.props.children;
		}

		return (
			<Box
				display="flex"
				alignItems="center"
				justifyContent="center"
				w="100%"
				h="100%"
				p={8}
				bgColor="gray.50"
			>
				<VStack spacing={4} maxW="500px">
					<HStack spacing={3} color="orange.500">
						<Icon as={AlertTriangle} boxSize={8} />
						<Heading size="lg" color="gray.800">Unable to Load Design</Heading>
					</HStack>
					<Text color="gray.600" textAlign="center">
						This design could not be loaded due to an error. Please select another design from the tabs below.
					</Text>
					{import.meta.env.DEV && (
						<Box pt={4} borderTop="1px solid" borderColor="gray.200" w="100%">
							<Code p={3} borderRadius="md" fontSize="xs" display="block" whiteSpace="pre-wrap">
								{this.state.error?.message}
							</Code>
						</Box>
					)}
				</VStack>
			</Box>
		);
	}
}
